import React, { useEffect, useRef, useState } from 'react';
import styles from './ListOfUsers.module.css';
import ListOfUsersTable from '../../components/ListOfUsersTable/ListOfUsersTable';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

export default function ListOfUsers() {
    const [allUsersShown, setAllUsersShown] = useState(true);
    const [approvalShown, setApprovalShown] = useState(false);
    const [managementShown, setManagementShown] = useState(false);
    const [supplierShown, setSupplierShown] = useState(false);
    const [activeTab, setActiveTab] = useState('allUsers');
    const [showColumnFilters, setShowColumnFilters] = useState(false);
    const [globalFilter, setGlobalFilter] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);
    const [showColumnPicker, setShowColumnPicker] = useState(false);
    const [columnOrder, setColumnOrder] = useState(['name', 'organization', 'email', 'accessLevel', 'tier']);
    const [draggedColumnIndex, setDraggedColumnIndex] = useState(null);
    const searchInputRef = useRef(null);
    const columnPickerRef = useRef(null);

    const moveColumn = (fromIndex, toIndex) => {
        if (fromIndex === null || toIndex === null || fromIndex === toIndex) return;
        if (toIndex < 0 || toIndex >= columnOrder.length) return;
        setColumnOrder((prev) => {
            const updated = [...prev];
            const [moved] = updated.splice(fromIndex, 1);
            updated.splice(toIndex, 0, moved);
            return updated;
        });
    };

    const formatColumnName = (key) => (key === 'accessLevel' ? 'Access Level' : key.charAt(0).toUpperCase() + key.slice(1));

    const handleAllUsersToggle = () => {
        setAllUsersShown(true);
        setApprovalShown(false);
        setManagementShown(false);
        setSupplierShown(false);
        setActiveTab('allUsers');
    };

    const handleApprovalToggle = () => {
        setAllUsersShown(false);
        setApprovalShown(true);
        setManagementShown(false);
        setSupplierShown(false);
        setActiveTab('approvers');
    };

    const handleManagementToggle = () => {
        setAllUsersShown(false);
        setApprovalShown(false);
        setManagementShown(true);
        setSupplierShown(false);
        setActiveTab('managers');
    };

    const handleSupplierToggle = () => {
        setAllUsersShown(false);
        setApprovalShown(false);
        setManagementShown(false);
        setSupplierShown(true);
        setActiveTab('suppliers');
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (columnPickerRef.current && !columnPickerRef.current.contains(event.target)) {
                setShowColumnPicker(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className={styles.pageContainer}>
            <h2 className={styles.title}>List of Users</h2>
            <div className={styles.header}>
                <div className={styles.statusContainer}>
                    <div className={styles.statusRow}>
                        <button onClick={handleAllUsersToggle} className={`${styles.statusButton} ${allUsersShown ? styles.selected : styles.notSelected}`}>
                            All
                        </button>
                    </div>

                    <div className={styles.statusRow}>
                        <button onClick={handleApprovalToggle} className={`${styles.statusButton} ${approvalShown ? styles.selected : styles.notSelected}`}>
                            Approvers
                        </button>
                    </div>

                    <div className={styles.statusRow}>
                        <button onClick={handleManagementToggle} className={`${styles.statusButton} ${managementShown ? styles.selected : styles.notSelected}`}>
                            Managers
                        </button>
                    </div>

                    <div className={styles.statusRow}>
                        <button onClick={handleSupplierToggle} className={`${styles.statusButton} ${supplierShown ? styles.selected : styles.notSelected}`}>
                            Suppliers/Employees
                        </button>
                    </div>
                </div>
                <div className={styles.controlsContainer}>
                    <button
                        className={styles.externalControlButton}
                        onClick={() => setShowColumnFilters((prev) => !prev)}
                    >
                        <TuneIcon fontSize="small" />
                        {showColumnFilters ? 'Hide Filters' : 'Show Filters'}
                    </button>
                    {showSearchInput ? (
                        <input
                            ref={searchInputRef}
                            className={styles.searchInlineInput}
                            type="text"
                            placeholder="Search users..."
                            value={globalFilter || ''}
                            onChange={(e) => setGlobalFilter(e.target.value)}
                            onBlur={() => {
                                if (!globalFilter) setShowSearchInput(false);
                            }}
                        />
                    ) : (
                        <button
                            className={styles.externalControlButton}
                            onClick={() => {
                                setShowSearchInput(true);
                                setTimeout(() => searchInputRef.current?.focus(), 0);
                            }}
                        >
                            <SearchIcon fontSize="small" />
                            Search
                        </button>
                    )}
                    <div className={styles.columnPickerAnchor} ref={columnPickerRef}>
                        <button
                            className={styles.externalControlButton}
                            onClick={() => setShowColumnPicker((prev) => !prev)}
                        >
                            Columns
                        </button>
                        {showColumnPicker && (
                            <div className={styles.columnPicker}>
                                <button
                                    type="button"
                                    className={styles.resetOrderButton}
                                    onClick={() => setColumnOrder(['name', 'organization', 'email', 'accessLevel', 'tier'])}
                                >
                                    Reset Order
                                </button>
                                <div className={styles.columnList}>
                                    {columnOrder.map((key, index) => (
                                        <div
                                            key={key}
                                            className={styles.columnOption}
                                            draggable
                                            onDragStart={() => setDraggedColumnIndex(index)}
                                            onDragOver={(event) => event.preventDefault()}
                                            onDrop={() => {
                                                moveColumn(draggedColumnIndex, index);
                                                setDraggedColumnIndex(null);
                                            }}
                                            onDragEnd={() => setDraggedColumnIndex(null)}
                                        >
                                            <div className={styles.columnLabelWrap}>
                                                <DragIndicatorIcon className={styles.dragHandle} />
                                                <span className={styles.columnName}>{formatColumnName(key)}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <ListOfUsersTable
                activeTab={activeTab}
                showColumnFilters={showColumnFilters}
                globalFilter={globalFilter}
                onGlobalFilterChange={setGlobalFilter}
                columnOrder={columnOrder}
            />
        </div>
    )
}
